/**
 * Error log: what went wrong, rather than what someone did.
 *
 * The companion to the Audit log, and deliberately the same shape — same filter
 * panel, same table, same CSV — because they answer the two halves of one question.
 * The audit log only ever gets a row when an action succeeded; this is where the
 * other outcome goes.
 *
 * Most of what lands here used to be lost. A PDF whose text could not be read still
 * hands its pictures back, so the failure showed as one line in the import modal
 * and then disappeared with the modal. Nobody could ask afterwards how often that
 * happens or to which files. Now it is a row, and the recurring panel groups the
 * fifty copies of one problem into the one problem they are.
 */
import { useEffect, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { PageHeader } from '../App'
import { useApi, qs, api, useApiMutation, fetchAllRows } from '../lib/api'
import { EnumSelect, Picker } from '../components/Select'
import { DateRangeField, DatePresets } from '../components/DateField'
import { TrendChart } from '../components/charts'
import {
  Card, Tile, Field, Badge, Empty, Loading, ErrorState, Modal, ConfirmModal, Notice,
  Filters, FilterActions, countFilters, SearchInput, useSort, fmtInt, fmtBytes, fmtDateTime,
  downloadCsv, useToast,
} from '../components/ui'
import { PagedTable } from '../components/DataTable'

/**
 * A source is a dotted path so it sorts and filters usefully; these are the names
 * for reading. Anything unmapped falls through as its raw key rather than being
 * hidden, so a source added in the Worker shows up here without a change.
 */
const SOURCE_LABEL = {
  'pdf.text-layer': 'PDF text layer',
  'pdf.ai-convert': 'PDF, AI conversion',
  'pdf.pictures': 'PDF pictures',
  'pdf.import': 'PDF import',
  'vision.ocr': 'Scanned page OCR',
  'vision.transcribe': 'Photo transcription',
  'vision.caption': 'Image alt text',
  'ai.prompt': 'Prompt run',
  'image.resize': 'Image resize',
  search: 'Search index',
  api: 'API request',
  'donor.sign-in': 'Donor sign-in',
  'client.render': 'Browser, page',
  'client.import': 'Browser, import',
  'client.upload': 'Browser, upload',
  'client.network': 'Browser, network',
  'client.other': 'Browser',
}

const sourceLabel = (s) => SOURCE_LABEL[s] ?? s

/**
 * Two-letter country codes into country names, in the browser rather than the database.
 *
 * The Worker records what Cloudflare's edge gives it, which is a code — see
 * clientGeo() in src/worker/util.js — and `GB` is not something anybody should have to
 * translate while reading a log. `Intl.DisplayNames` is the runtime's own list, so this
 * costs nothing to carry and stays right as the list changes. Built once: constructing
 * one per row is the expensive part of using it.
 */
const REGION_NAMES = (() => {
  try {
    return new Intl.DisplayNames(['en-GB'], { type: 'region' })
  } catch {
    return null
  }
})()

/**
 * The name for a country code, or the code itself when there is no name to be had.
 *
 * `of()` throws on anything that is not shaped like a region, which the edge does
 * legitimately return — it has a couple of codes for callers it will not place in a
 * real country. Those are still worth showing as they are: "the edge would not say
 * where this came from" is itself the useful fact about such a row.
 */
const countryName = (code) => {
  if (!code) return null
  try {
    return REGION_NAMES?.of(code) || code
  } catch {
    return code
  }
}

/**
 * Where a caller was, as one line: "Nairobi, Kenya", or just the country when the edge
 * placed the address no more closely than that — which is the common case, and reads
 * better than a lone comma or an invented "Unknown city".
 */
const placeLabel = (r) => [r.city, countryName(r.country)].filter(Boolean).join(', ') || null

/** The reading paths — a PDF or an image that would not turn into text. */
const isExtraction = (s) => String(s).startsWith('pdf.') || String(s).startsWith('vision.')

const EMPTY = {
  source: null, severity: null, status: 'open', area: null, user: null, host: null, ip: null,
  country: null, attempted: null, model: null, fingerprint: '', from: '', to: '', q: '',
}

/**
 * The filters a link may arrive with, read off the query string.
 *
 * Other pages count failures of their own and the count is only useful if it leads to
 * the rows behind it — the Failures column on the AI usage report links here per model.
 * Only the filters worth pointing at from elsewhere are read; anything else keeps the
 * page's own default.
 *
 * `status` is the one that needs saying twice. The log opens on the unresolved rows,
 * which is right for someone arriving to work through it and wrong for someone arriving
 * from a number that was counted over every row — so `status=all` says "both", and a
 * link that mentions no status still gets the default it would have had.
 */
const fromUrl = (params) => {
  const val = (k) => params.get(k) || null
  const severity = params.get('severity')
  const status = params.get('status')
  return {
    ...EMPTY,
    source: val('source'),
    severity: severity === 'error' || severity === 'warning' ? severity : null,
    status: status === null ? EMPTY.status : status === 'open' || status === 'resolved' ? status : null,
    model: val('model'),
    country: val('country'),
    user: val('user'),
    attempted: val('attempted'),
    from: params.get('from') ?? '',
    to: params.get('to') ?? '',
    q: params.get('q') ?? '',
  }
}

const STATE_LABEL = { open: 'Unresolved', resolved: 'Resolved' }
const SEVERITY_LABEL = { error: 'Errors', warning: 'Warnings' }

/**
 * The filters as sentences, for the one place they have to be read rather than used:
 * the confirmation before resolving everything they match. A count on its own — "resolve
 * 240 errors" — is not something anyone can check, because the whole question is 240 of
 * *which*, and the panel that would answer it folds shut and is usually shut.
 */
const describeFilters = (f) =>
  Object.entries(f)
    .filter(([, v]) => v !== null && v !== undefined && v !== '')
    .map(([k, v]) => {
      switch (k) {
        case 'source': return `Source: ${sourceLabel(v)}`
        case 'severity': return `Severity: ${SEVERITY_LABEL[v] ?? v}`
        case 'status': return `State: ${STATE_LABEL[v] ?? v}`
        case 'area': return 'Area: PDF & image text extraction'
        case 'user': return `User: ${v}`
        case 'host': return `Hostname: ${v}`
        case 'ip': return `Address: ${v}`
        // The name, not the code. This sentence is read once, in the confirmation before
        // resolving everything the filters match, and "Country: TR" is the wrong thing to
        // be squinting at while deciding whether to mark two hundred rows read.
        case 'country': return `Country: ${countryName(v)}`
        case 'attempted': return `Sign-in attempted as: ${v}`
        case 'model': return `Model: ${v}`
        case 'fingerprint': return 'Only one recurring failure'
        case 'from': return `On or after ${v}`
        case 'to': return `On or before ${v}`
        case 'q': return `Message, detail, filename or attempted email contains “${v}”`
        default: return `${k}: ${v}`
      }
    })

export default function ErrorLog({ user }) {
  const [params] = useSearchParams()
  const search = params.toString()
  const [f, setF] = useState(() => fromUrl(params))
  const [page, setPage] = useState(1)
  const [detail, setDetail] = useState(null)
  const [purging, setPurging] = useState(false)
  const [resolvingAll, setResolvingAll] = useState(false)
  const toast = useToast()

  const set = (patch) => {
    setF((prev) => ({ ...prev, ...patch }))
    setPage(1)
  }

  /*
   * The panel owns the filters once the page is up — they are not written back to the
   * URL, so clearing one that arrived in a link does not fight the address bar. But a
   * second link to this same route only changes the query string and leaves the page
   * mounted, which would otherwise show the first link's filters, so a genuinely new
   * query string is read again. Same arrangement as the updates list.
   */
  const appliedSearch = useRef(search)
  useEffect(() => {
    if (appliedSearch.current === search) return
    appliedSearch.current = search
    setF(fromUrl(new URLSearchParams(search)))
    setPage(1)
  }, [search])

  const [limit, setLimit] = useState(100)
  const { sort, dir, onSort: sortBy } = useSort('created_at', 'desc', ['created_at'])
  const onSort = (key) => {
    sortBy(key)
    setPage(1)
  }
  const query = useApi(['errors', f, page, limit, sort, dir], `errors${qs({ ...f, page, limit, sort, dir })}`, {
    keepPreviousData: true,
  })

  // Every failure these filters match, in this order — not only the page on screen.
  const exportAll = async () => {
    try {
      const all = await fetchAllRows('errors', { ...f, sort, dir })
      downloadCsv('error-log.csv', all.rows, [
                { key: 'created_at', label: 'When' },
                { key: 'source', label: 'Source' },
                { key: 'severity', label: 'Severity' },
                { key: 'message', label: 'Message' },
                { key: 'filename', label: 'File' },
                { key: 'model_key', label: 'Model' },
                { key: 'http_status', label: 'Status' },
                { key: 'host', label: 'Host' },
                { key: 'path', label: 'Path' },
                { key: 'user_email', label: 'User' },
                { key: 'attempted_email', label: 'Sign-in attempted as' },
                { key: 'ip', label: 'Address' },
                // The two the Worker stored, not the sentence the table shows: a
                // spreadsheet wants to group by country, which a "Nairobi, Kenya"
                // column cannot be asked to do.
                { key: 'country', label: 'Country' },
                { key: 'city', label: 'City' },
                { key: 'resolved_at', label: 'Resolved' },
      ])
      if (all.total > all.rows.length) toast.info(`Exported the first ${fmtInt(all.rows.length)} of ${fmtInt(all.total)} rows`)
    } catch (err) {
      toast.error(err)
    }
  }

  // Two facets are stored as keys and read as names — a dotted source and a two-letter
  // country code. Everything else is already the thing it says.
  const facetLabel = (kind, value) =>
    kind === 'source' ? sourceLabel(value) : kind === 'country' ? countryName(value) : value

  const facet = (kind) =>
    (query.data?.facets ?? [])
      .filter((x) => x.facet === kind && x.value)
      .map((x) => ({ value: x.value, label: `${facetLabel(kind, x.value)} (${x.n})` }))

  const resolve = useApiMutation(
    ({ id, resolved }) => api(`errors/${id}`, { method: 'PUT', body: { resolved } }),
    ['errors']
  )
  const resolveGroup = useApiMutation(
    (fingerprint) => api('errors/resolve', { method: 'POST', body: { fingerprint } }),
    ['errors']
  )
  // The filters go along as the query string the listing itself uses, so the server
  // narrows down to the same rows rather than being handed a list of ids from the one
  // page that happens to be on screen.
  const resolveMatching = useApiMutation(
    (filters) => api(`errors/resolve${qs(filters)}`, { method: 'POST', body: { all: true } }),
    ['errors']
  )
  const purge = useApiMutation(() => api('errors?resolved=1', { method: 'DELETE' }), ['errors'])

  const summary = query.data?.summary
  // `place` worked out here rather than in the cell that shows it, which would otherwise
  // ask for the same string three times over — once to show, once for the row's tooltip,
  // once to decide whether the cell is empty enough to need a dash.
  const rows = (query.data?.rows ?? []).map((r) => ({ ...r, place: placeLabel(r) }))
  const canResolve = !!user?.caps?.update
  // Everything the filters match that nobody has read yet — not rows.length, which is
  // one page of it, and not summary.open, which ignores the filters entirely.
  const matchingOpen = query.data?.matchingOpen ?? 0
  const applied = describeFilters(f)

  const columns = [
    { key: 'created_at', label: 'When', sortKey: 'created_at', width: 160, cellClass: 'nowrap small', render: (r) => fmtDateTime(r.created_at) },
    {
      key: 'source',
      label: 'Source',
      sortKey: 'source',
      width: 150,
      cellClass: 'small',
      render: (r) => (
        <Badge tone={r.severity === 'warning' ? 'warn' : 'danger'} title={`${r.source} · ${r.severity}`}>
          {sourceLabel(r.source)}
        </Badge>
      ),
    },
    {
      key: 'message',
      label: 'What happened',
      cellClass: 'small',
      render: (r) => (
        <>
          {r.message}
          {r.http_status ? <span className="subtle mono tiny"> · {r.http_status}</span> : null}
        </>
      ),
    },
    {
      key: 'filename',
      label: 'File / where',
      sortKey: 'filename',
      width: 160,
      cellClass: 'small truncate',
      render: (r) => (
        <span title={r.filename || [r.host, r.path].filter(Boolean).join('') || ''}>
          {r.filename ? (
            r.filename
          ) : r.entity === 'newsletter' && r.entity_id ? (
            <Link to={`/newsletters/${r.entity_id}`}>update #{r.entity_id}</Link>
          ) : r.path ? (
            // The hostname above the path rather than joined to it: in one column's width a
            // joined string truncates away exactly the end that says which endpoint failed.
            // Rows logged before the hostname was recorded simply have no first line.
            <>
              {r.host ? <div className="mono tiny subtle truncate">{r.host}</div> : null}
              <span className="mono tiny subtle">{r.path}</span>
            </>
          ) : (
            '—'
          )}
        </span>
      ),
    },
    {
      key: 'user_email',
      label: 'Who',
      sortKey: 'user_email',
      width: 170,
      cellClass: 'small truncate',
      // All three, because on the rows that matter most they are never all there: a
      // failed sign-in has an address and no name, and a PDF that would not open has a
      // name and no request behind it. The place is the line that can be read at a
      // glance — an address says which caller, and only the country says whether that
      // caller has any business here. `mono` sits on the identifiers rather than the
      // cell, since a city in a monospace face reads as data.
      render: (r) => (
        <div
          className="truncate"
          title={[r.user_email, r.attempted_email && `tried ${r.attempted_email}`, r.ip, r.place]
            .filter(Boolean)
            .join(' · ')}
        >
          <span className="mono">{r.user_email || (r.attempted_email || r.ip || r.place ? '' : '—')}</span>
          {/* Never in the name's place without saying so: this is what was typed into the
              form, not who it was. */}
          {!r.user_email && r.attempted_email ? (
            <div className="small">
              <span className="subtle">tried </span>
              <span className="mono">{r.attempted_email}</span>
            </div>
          ) : null}
          {r.ip ? <div className="mono tiny subtle">{r.ip}</div> : null}
          {r.place ? <div className="tiny subtle">{r.place}</div> : null}
        </div>
      ),
    },
    {
      key: 'actions',
      label: '',
      width: 180,
      render: (r) => (
        <div className="row row-tight">
          <button className="btn btn-sm" onClick={() => setDetail(r)}>
            Details
          </button>
          {canResolve && (
            <button
              className="btn btn-sm"
              disabled={resolve.isPending}
              onClick={() =>
                resolve.mutate(
                  { id: r.id, resolved: !r.resolved_at },
                  { onError: (err) => toast.error(err.message) }
                )
              }
            >
              {r.resolved_at ? 'Reopen' : 'Resolve'}
            </button>
          )}
        </div>
      ),
    },
  ]

  return (
    <PageHeader
      title="Error Log"
      sub={
        summary
          ? `${fmtInt(summary.open)} unresolved · ${fmtInt(summary.total)} recorded`
          : 'Loading…'
      }
      actions={
        <>
          {/* Up here rather than in the filter panel, which folds shut and is shut by
              default — an action that only exists while the controls it belongs to are
              unfolded is an action nobody finds. The count is what ties it to the
              filters instead, and it disappears when there is nothing left to resolve. */}
          {canResolve && matchingOpen > 0 && (
            <button
              className="btn btn-sm"
              disabled={resolveMatching.isPending}
              onClick={() => setResolvingAll(true)}
              title={
                applied.length
                  ? `Every unresolved error matching the filters now applied`
                  : 'Every unresolved error in the log'
              }
            >
              Resolve {fmtInt(matchingOpen)} matching
            </button>
          )}
          {user?.caps?.delete && (
            <button className="btn btn-sm" onClick={() => setPurging(true)}>
              Clear resolved
            </button>
          )}
          <button className="btn btn-sm" onClick={exportAll} disabled={!query.data?.total}>
            ↓ CSV
          </button>
        </>
      }
    >
      <div className="col">
        {summary && (
          <div className="grid grid-4">
            <Tile
              label="Unresolved"
              value={fmtInt(summary.open)}
              tone={summary.open ? 'danger' : undefined}
              note={summary.open ? 'Waiting to be looked at' : 'Nothing outstanding'}
            />
            <Tile
              label="Last 24 hours"
              value={fmtInt(summary.last_24h)}
              note={`${fmtInt(summary.last_7d)} in the last 7 days`}
            />
            <Tile
              label="Reading failures"
              value={fmtInt(summary.extraction_7d)}
              tone={summary.extraction_7d ? 'warn' : undefined}
              note={`PDF and image text extraction, 7 days · ${fmtInt(summary.extraction)} all time`}
            />
            <Tile
              label="Warnings"
              value={fmtInt(summary.warnings)}
              note={`${fmtInt(summary.errors)} outright failures`}
            />
          </div>
        )}

        {/* Above the panel, as on every list: the box is the filter people reach for first,
            and it should not be behind a click. */}
        <SearchInput
          value={f.q}
          onChange={(v) => set({ q: v })}
          placeholder="Search the message, detail, filename or attempted email…"
          aria-label="Search the error log"
        />

        <Filters id="errors" active={countFilters(f)} onClear={() => set(EMPTY)}>
          <Field label="Source" htmlFor="e-source">
            <Picker
              inputId="e-source"
              value={f.source}
              onChange={(v) => set({ source: v })}
              options={facet('source')}
              placeholder="Any"
            />
          </Field>
          <Field label="Severity" htmlFor="e-severity">
            <EnumSelect
              inputId="e-severity"
              value={f.severity}
              onChange={(v) => set({ severity: v })}
              options={[
                { value: 'error', label: 'Errors — something did not work' },
                { value: 'warning', label: 'Warnings — recovered from' },
              ]}
            />
          </Field>
          <Field label="State" htmlFor="e-status">
            <EnumSelect
              inputId="e-status"
              value={f.status}
              onChange={(v) => set({ status: v })}
              options={[
                { value: 'open', label: 'Unresolved' },
                { value: 'resolved', label: 'Resolved' },
              ]}
            />
          </Field>
          <Field label="Area" htmlFor="e-area">
            <EnumSelect
              inputId="e-area"
              value={f.area}
              onChange={(v) => set({ area: v })}
              options={[{ value: 'extraction', label: 'PDF & image text extraction' }]}
            />
          </Field>
          {/* Only the models that have actually failed are in the list, which is what
              makes it worth having: it is also where a filter arriving from the AI usage
              report shows itself, so it can be seen and cleared like any other. */}
          <Field label="Model" htmlFor="e-model">
            <Picker
              inputId="e-model"
              value={f.model}
              onChange={(v) => set({ model: v })}
              options={facet('model')}
              placeholder="Any"
            />
          </Field>
          <Field label="User" htmlFor="e-user">
            <Picker
              inputId="e-user"
              value={f.user}
              onChange={(v) => set({ user: v })}
              options={facet('user')}
              placeholder="Any"
            />
          </Field>
          {/* Which door the request came to. The archive answers on two hostnames and the
              same path means different things at each: a 401 on the apex is a staff
              session that has run out, and on the donor host it is somebody who is not
              signed in at all. */}
          <Field label="Hostname" htmlFor="e-host">
            <Picker
              inputId="e-host"
              value={f.host}
              onChange={(v) => set({ host: v })}
              options={facet('host')}
              placeholder="Any"
            />
          </Field>
          {/* The busiest twenty addresses, which is what the API offers — a list of
              every address that ever failed would be unbounded and mostly noise. Any
              other one is one click away on the row it appears in. */}
          <Field label="Address" htmlFor="e-ip">
            <Picker
              inputId="e-ip"
              value={f.ip}
              onChange={(v) => set({ ip: v })}
              options={facet('ip')}
              placeholder="Any"
            />
          </Field>
          {/* The coarse half of the same question, and the half worth asking first: an
              address narrows to one caller, a country to a whole burst of them. Every
              country that has ever failed is in the list, not the busiest few — the
              single request from somewhere unexpected is the one to find. Named rather
              than coded, so the list can be read down. */}
          <Field label="Country" htmlFor="e-country">
            <Picker
              inputId="e-country"
              value={f.country}
              onChange={(v) => set({ country: v })}
              options={facet('country')}
              placeholder="Any"
            />
          </Field>
          {/* The account a refused sign-in was aimed at — typed by whoever was at the
              form, so kept apart from User, which only ever holds somebody verified.
              The twenty most-tried, like Address; any other is a click on its row. */}
          <Field label="Sign-in attempted as" htmlFor="e-attempted">
            <Picker
              inputId="e-attempted"
              value={f.attempted}
              onChange={(v) => set({ attempted: v })}
              options={facet('attempted')}
              placeholder="Any"
            />
          </Field>
          <Field label="Date range">
            <DateRangeField
              from={f.from}
              to={f.to}
              onFrom={(v) => set({ from: v })}
              onTo={(v) => set({ to: v })}
            />
          </Field>
          <FilterActions active={countFilters(f)} onClear={() => set(EMPTY)} busy={query.isFetching}>
            <DatePresets onPick={(from, to) => set({ from: from ?? '', to: to ?? '' })} />
          </FilterActions>
        </Filters>

        {query.isLoading && (
          <Card>
            <Loading />
          </Card>
        )}
        {query.isError && (
          <Card>
            <ErrorState error={query.error} retry={query.refetch} />
          </Card>
        )}

        {f.fingerprint && (
          <Notice tone="info" icon="repeat" title="Showing one recurring failure">
            Every occurrence of the problem you picked from the recurring panel below.{' '}
            <button className="btn btn-sm" onClick={() => set({ fingerprint: '' })}>
              Show everything again
            </button>
          </Notice>
        )}

        {query.data && !rows.length && (
          <Card>
            <Empty icon="flag" title={countFilters(f) ? 'Nothing matches those filters' : 'Nothing has failed'}>
              {countFilters(f) ? (
                'Widen the filters, or clear them to see the whole log.'
              ) : (
                <>
                  A PDF that will not give up its text, a vision model that will not answer, an
                  upload that fails on the way in — all of it lands here with the file, the model
                  and the person it happened to, instead of vanishing with the dialog that showed it.
                </>
              )}
            </Empty>
          </Card>
        )}

        {rows.length ? (
          <PagedTable
            compact
            data={query.data}
            columns={columns}
            rowClass={(r) => (r.resolved_at ? 'settled' : undefined)}
            sort={sort}
            dir={dir}
            onSort={onSort}
            onPage={setPage}
            onLimit={(n) => {
              setLimit(n)
              setPage(1)
            }}
          />
        ) : null}

        {query.data?.byDay?.length > 1 && (
          <Card title="Failures per Day" sub="Last 30 days">
            <TrendChart
              data={query.data.byDay}
              x="day"
              series={[
                { key: 'n', label: 'All' },
                { key: 'errors', label: 'Errors' },
                { key: 'extraction', label: 'Reading failures' },
              ]}
              height={220}
            />
          </Card>
        )}

        {query.data?.recurring?.length ? (
          <Card
            title="Recurring"
            sub="The same failure more than once — grouped by what it is, not which file it happened to"
          >
            <div className="table-wrap flush">
              <table className="data compact">
                <thead>
                  <tr>
                    <th style={{ width: 150 }}>Source</th>
                    <th>Message</th>
                    <th style={{ width: 70 }} className="right">
                      Times
                    </th>
                    <th style={{ width: 150 }}>Last seen</th>
                    {/* Wide enough for both buttons on one line — at 160 they wrapped
                        onto two and doubled the height of every row. */}
                    <th style={{ width: 210 }} />
                  </tr>
                </thead>
                <tbody>
                  {query.data.recurring.map((g) => (
                    <tr key={g.fingerprint}>
                      <td className="small">
                        <Badge tone={isExtraction(g.source) ? 'warn' : undefined}>
                          {sourceLabel(g.source)}
                        </Badge>
                      </td>
                      <td className="small muted">{g.message}</td>
                      <td className="num">{fmtInt(g.n)}</td>
                      <td className="nowrap small">{fmtDateTime(g.last_seen)}</td>
                      <td>
                        <div className="row row-tight">
                          {/* Back to the top with it: the rows this narrows are the
                              table above this panel, so pressing Show here otherwise
                              changes something off the screen and reads as a no-op. */}
                          <button
                            className="btn btn-sm"
                            onClick={() => {
                              set({ fingerprint: g.fingerprint, status: null })
                              window.scrollTo({ top: 0, behavior: 'smooth' })
                            }}
                          >
                            Show {fmtInt(g.n)}
                          </button>
                          {canResolve && g.open > 0 && (
                            <button
                              className="btn btn-sm"
                              disabled={resolveGroup.isPending}
                              onClick={() =>
                                resolveGroup.mutate(g.fingerprint, {
                                  onSuccess: (r) => toast.ok(`Resolved ${r.resolved} error(s)`),
                                  onError: (err) => toast.error(err.message),
                                })
                              }
                            >
                              Resolve {fmtInt(g.open)}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        ) : null}
      </div>

      <Modal open={!!detail} onClose={() => setDetail(null)} title="What went wrong" size="lg">
        {detail && (
          <div className="col">
            <Notice
              tone={detail.severity === 'warning' ? 'warn' : 'danger'}
              icon={detail.severity === 'warning' ? 'warning' : 'error'}
              title={sourceLabel(detail.source)}
            >
              {detail.message}
            </Notice>
            <dl className="kv">
              <dt>When</dt>
              <dd>{fmtDateTime(detail.created_at)}</dd>
              <dt>Source</dt>
              <dd className="mono small">{detail.source}</dd>
              <dt>Severity</dt>
              <dd>{detail.severity}</dd>
              {detail.user_email && (
                <>
                  <dt>User</dt>
                  <dd className="mono small">{detail.user_email}</dd>
                </>
              )}
              {detail.attempted_email && (
                <>
                  <dt>Attempted as</dt>
                  {/* Every refusal aimed at this one account, whichever address it came
                      from — the reading that tells a donor who forgot their password from
                      somebody working through a list. */}
                  <dd className="row row-tight">
                    <span className="mono small">{detail.attempted_email}</span>
                    <button
                      className="btn btn-sm"
                      onClick={() => {
                        set({ attempted: detail.attempted_email, status: null })
                        setDetail(null)
                      }}
                    >
                      Show every attempt on it
                    </button>
                  </dd>
                </>
              )}
              {detail.ip && (
                <>
                  <dt>Address</dt>
                  <dd className="row row-tight">
                    <span className="mono small">{detail.ip}</span>
                    <button
                      className="btn btn-sm"
                      onClick={() => {
                        set({ ip: detail.ip, status: null })
                        setDetail(null)
                      }}
                    >
                      Show everything from it
                    </button>
                  </dd>
                </>
              )}
              {/* Its own line rather than a parenthesis after the address, because it is
                  often the half that is there: the edge places nearly every address in a
                  country, and a row logged before this was recorded has neither. */}
              {placeLabel(detail) && (
                <>
                  <dt>Location</dt>
                  {/* The same offer the address makes one line up, widened: that one
                      follows a single caller, this one follows the place, which is what
                      to reach for when a burst is spread over addresses. */}
                  <dd className="row row-tight">
                    <span className="small">{placeLabel(detail)}</span>
                    {detail.country && (
                      <button
                        className="btn btn-sm"
                        onClick={() => {
                          set({ country: detail.country, status: null })
                          setDetail(null)
                        }}
                      >
                        Show everything from {countryName(detail.country)}
                      </button>
                    )}
                  </dd>
                </>
              )}
              {detail.filename && (
                <>
                  <dt>File</dt>
                  <dd className="small">
                    {detail.filename}
                    {detail.mime_type ? ` · ${detail.mime_type}` : ''}
                    {detail.bytes ? ` · ${fmtBytes(detail.bytes)}` : ''}
                  </dd>
                </>
              )}
              {detail.model_key && (
                <>
                  <dt>Model</dt>
                  <dd className="mono small">
                    {detail.model_key}
                    {detail.provider_key ? ` (${detail.provider_key})` : ''}
                  </dd>
                </>
              )}
              {detail.field_key && (
                <>
                  <dt>Field</dt>
                  <dd className="mono small">{detail.field_key}</dd>
                </>
              )}
              {detail.entity && (
                <>
                  <dt>Record</dt>
                  <dd className="small">
                    {detail.entity === 'newsletter' && detail.entity_id ? (
                      <Link to={`/newsletters/${detail.entity_id}`}>
                        {detail.entity} #{detail.entity_id}
                      </Link>
                    ) : (
                      `${detail.entity}${detail.entity_id ? ` #${detail.entity_id}` : ''}`
                    )}
                  </dd>
                </>
              )}
              {(detail.path || detail.host) && (
                <>
                  <dt>Request</dt>
                  {/* Whole address, hostname and all, so it can be read or pasted as the
                      thing that was actually called rather than reconstructed from which
                      door you assumed it came to. */}
                  <dd className="mono small">
                    {detail.method} {detail.host ?? ''}
                    {detail.path ?? ''}
                    {detail.http_status ? ` → ${detail.http_status}` : ''}
                  </dd>
                </>
              )}
              {detail.resolved_at && (
                <>
                  <dt>Resolved</dt>
                  <dd className="small">
                    {fmtDateTime(detail.resolved_at)} by {detail.resolved_by}
                  </dd>
                </>
              )}
            </dl>

            {detail.context_json && (
              <div>
                <div className="field-label">Circumstances</div>
                <Pre>{pretty(detail.context_json)}</Pre>
              </div>
            )}
            {detail.detail && (
              <div>
                <div className="field-label">Detail</div>
                <Pre>{detail.detail}</Pre>
              </div>
            )}
            <p className="tiny subtle">
              Resolving an error marks it as read, not as fixed — the row stays, so the recurring
              panel can still tell you this problem has happened before.
            </p>
          </div>
        )}
      </Modal>

      <ConfirmModal
        open={resolvingAll}
        onClose={() => setResolvingAll(false)}
        busy={resolveMatching.isPending}
        title="Resolve everything matching"
        confirmLabel={`Resolve ${fmtInt(matchingOpen)}`}
        body={
          <div className="col">
            <p>
              Marks {fmtInt(matchingOpen)} unresolved error
              {matchingOpen === 1 ? '' : 's'} as read — every one the filters match, not
              only the {fmtInt(rows.length)} on this page.
            </p>
            {applied.length ? (
              <>
                <div className="field-label">Filters applied</div>
                <ul className="small">
                  {applied.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              </>
            ) : (
              <Notice tone="warn" icon="warning" title="No filters are applied">
                This is every unresolved error in the log, including ones nobody has read.
              </Notice>
            )}
            <p className="tiny subtle">
              Nothing is deleted. Resolving is a note that someone has looked, and any row can
              be reopened from its own line afterwards.
            </p>
          </div>
        }
        onConfirm={() =>
          resolveMatching.mutate(f, {
            onSuccess: (r) => {
              toast.ok(`Resolved ${fmtInt(r.resolved)} error(s)`)
              setResolvingAll(false)
            },
            onError: (err) => toast.error(err.message),
          })
        }
      />

      <ConfirmModal
        open={purging}
        onClose={() => setPurging(false)}
        busy={purge.isPending}
        title="Clear resolved errors"
        confirmLabel="Clear them"
        body={
          <p>
            Deletes every error already marked resolved. Unresolved ones are kept — nothing that
            has not been read is thrown away.
          </p>
        }
        onConfirm={() =>
          purge.mutate(undefined, {
            onSuccess: (r) => {
              toast.ok(`Cleared ${r.deleted} resolved error(s)`)
              setPurging(false)
            },
            onError: (err) => toast.error(err.message),
          })
        }
      />
    </PageHeader>
  )
}

const Pre = ({ children }) => (
  <div
    className="mono small"
    style={{
      padding: 12,
      background: 'var(--bg-sunken)',
      borderRadius: 8,
      whiteSpace: 'pre-wrap',
      overflowX: 'auto',
      maxHeight: 320,
      overflowY: 'auto',
    }}
  >
    {children}
  </div>
)

const pretty = (raw) => {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2)
  } catch {
    return raw
  }
}
